import React, { useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import PageHeader from '../../components/PageHeader';
import FadeContent from '../../components/react-bits/FadeContent/FadeContent';
import './AIReview.css';

// AI审核：审核工作台（Demo）
// 顶部能力切换 → 左：审核对象 → 中：脚本/素材正文 → 右：AI审核结果
// 当前阶段无真实审核能力，正文与结果均为示例

type Capability = 'script' | 'material' | 'compliance';

interface ReviewCheck {
  label: string;
  value: string;
  tone: 'success' | 'warning' | 'info';
}

interface ReviewObject {
  id: string;
  name: string;
  meta: string;
  status: 'success' | 'warning';
  statusText: string;
  riskLevel: '低' | '中';
  content: string;
  summary: string;
  score: number;          // 0-10
  checks: ReviewCheck[];
  fixes: string[];
  suggestions: string[];
}

const capabilityTabs: Array<{ key: Capability; label: string; desc: string }> = [
  { key: 'script', label: '脚本审核', desc: '钩子 · 人群 · 剧情逻辑 · 承接 · 转化引导' },
  { key: 'material', label: '素材审核', desc: '表达抽查 · 授权 · 条件字幕完整性' },
  { key: 'compliance', label: '合规审核', desc: '绝对化用语 · 过度承诺 · 条件缺失' },
];

const reviewObjects: Record<Capability, ReviewObject[]> = {
  script: [
    {
      id: 's1', name: '《慢病可投清单口播》', meta: '40秒 · 口播脚本', status: 'success', statusText: '合规通过',
      riskLevel: '低', score: 8.2,
      content: '(0-3s 疾病清单图前贴)\n爸妈有高血压、糖尿病、结节，重疾险是不是买不了了？先别急着放弃。\n\n(4-10s 场景痛点)\n上个月陪我妈体检，报告出来那一刻，我第一反应是：是不是什么保险都买不了了。\n\n(11-20s 产品进入)\n像这款中老年百万重疾险，三大结节有机会投；高血压、糖尿病没有并发症，满足健康告知也有机会投。\n\n(21-32s 责任拆分)\n住院治疗10万、先进治疗50万、康复护理40万，按不同责任给付。\n\n(33-40s 条件+CTA)\n每月9.65元起，以页面测算为准。先点下方测一测能不能投。',
      summary: '钩子具体、慢病可投绑定健告条件、100万按责任拆分表达，可直接进入小样测试。',
      checks: [
        { label: '产品事实', value: '价格/健告/责任拆分均与Skill一致', tone: 'success' },
        { label: 'Hook', value: '1.3 / 1.5 · 点名父母与慢病', tone: 'success' },
        { label: '人群', value: '子女视角明确 · 25-45岁', tone: 'success' },
        { label: '产品承接', value: '1.2 / 1.5 · 责任拆分完整', tone: 'info' },
        { label: '合规', value: '2.0 / 2.0 · 无风险表达', tone: 'success' },
      ],
      fixes: ['结尾补充"以页面测算为准"字幕小字'],
      suggestions: ['责任拆分段可加字幕卡强调"按不同责任给付"'],
    },
    {
      id: 's2', name: '《异地子女高铁站直讲》', meta: '30秒 · 口播脚本', status: 'warning', statusText: '建议修改',
      riskLevel: '中', score: 7.4,
      content: '(0-3s 高铁站自拍)\n不能天天陪爸妈，至少先把大病保障看一眼。\n\n(4-15s 情绪展开)\n在外地工作第五年，最怕半夜接到家里的电话。\n\n(16-25s 产品进入)\n给爸妈补一层大病现金流保障，责任内最高100万。\n\n(26-30s 结尾)\n如果你也跟我一样，可以先了解一下。',
      summary: '情绪链路完整，但结尾引导偏弱，建议明确"点击测算"动作并补充条件小字。',
      checks: [
        { label: '产品事实', value: '保障表述准确 · 未拆责任', tone: 'warning' },
        { label: 'Hook', value: '1.2 / 1.5 · 异地情绪强', tone: 'success' },
        { label: '人群', value: '异地子女画像清晰', tone: 'success' },
        { label: '产品承接', value: '1.0 / 1.5 · 承接偏简略', tone: 'warning' },
        { label: '合规', value: '1.9 / 2.0 · 建议补等待期', tone: 'info' },
      ],
      fixes: ['结尾加明确CTA：点击测算', '补充90天等待期条件说明'],
      suggestions: ['中段增加一个具体场景（结算单/体检报告）增强信任'],
    },
  ],
  material: [
    {
      id: 'm1', name: '本月已上线素材抽查', meta: '抽查 3 条 · 头条信息流', status: 'warning', statusText: '1 条待复核',
      riskLevel: '中', score: 7.0,
      content: '抽查范围：当期在投素材 3 条（原生标题 2 条 + 明星二剪 1 条）。\n检查项：口播表达、字幕条件、授权范围、落地页一致性。',
      summary: '2 条素材表达与条件字幕完整；1 条口播中段口误"都能买"，已标记待复核。',
      checks: [
        { label: '抽查覆盖', value: '3 / 3 条完成', tone: 'success' },
        { label: '字幕条件完整', value: '3 / 3 · 价格与健告条件齐全', tone: 'success' },
        { label: '口播表达', value: '1 条存在"都能买"口误', tone: 'warning' },
      ],
      fixes: ['口误素材下线重剪', '复检报告归档'],
      suggestions: ['上线前增加口播逐句合规抽检环节'],
    },
    {
      id: 'm2', name: '明星二剪授权检查', meta: '授权素材 · 投放中', status: 'success', statusText: '授权有效',
      riskLevel: '低', score: 9.1,
      content: '检查范围：明星二剪授权素材及其衍生版本。\n检查项：授权有效期、衍生版本范围、条件字幕完整性。',
      summary: '授权在有效期内，衍生剪辑版本均在授权范围内，条件字幕未被裁切。',
      checks: [
        { label: '授权有效期', value: '覆盖当前投放周期', tone: 'success' },
        { label: '衍生版本', value: '2 个二剪版本均在授权内', tone: 'success' },
        { label: '条件字幕', value: '完整未被裁切', tone: 'success' },
      ],
      fixes: [],
      suggestions: ['授权到期前 30 天启动续期或替换'],
    },
  ],
  compliance: [
    {
      id: 'c1', name: '高风险表达扫描', meta: '当期全部在投文案', status: 'success', statusText: '未发现风险',
      riskLevel: '低', score: 9.5,
      content: '扫描范围：当期全部在投素材的口播、字幕、落地页文案。\n扫描规则：绝对化用语 / 过度承诺 / 条件缺失 / 责任表述。',
      summary: '未发现"都能买 / 100%覆盖 / 确诊就赔100万"等绝对化或过度承诺表达。',
      checks: [
        { label: '绝对化用语', value: '0 处命中', tone: 'success' },
        { label: '过度承诺', value: '0 处命中', tone: 'success' },
        { label: '责任拆分表达', value: '均按不同责任给付表述', tone: 'success' },
      ],
      fixes: [],
      suggestions: ['保持每周全量扫描一次'],
    },
    {
      id: 'c2', name: '必须条件检查', meta: '价格/健告/等待期', status: 'success', statusText: '条件齐全',
      riskLevel: '低', score: 9.0,
      content: '检查范围：当期素材的价格、健康告知、等待期三类必须条件表达。\n检查规则：价格必须带"起"和"以页面测算为准"；慢病可投必须绑定健告条件；等待期需说明。',
      summary: '价格表述均已补充"以页面测算为准"；慢病可投均绑定健康告知条件；等待期已说明。',
      checks: [
        { label: '价格条件', value: '9.65元起 + 测算为准', tone: 'success' },
        { label: '健康告知条件', value: '无并发症且满足健告', tone: 'success' },
        { label: '等待期', value: '90天 · 意外无等待期', tone: 'success' },
      ],
      fixes: [],
      suggestions: ['新素材上线前用条件检查清单过一遍'],
    },
  ],
};

const AIReview: React.FC = () => {
  const { currentProject } = useProject();
  const [capability, setCapability] = useState<Capability>('script');
  const [selectedId, setSelectedId] = useState<string>(reviewObjects.script[0].id);

  const objects = reviewObjects[capability];
  const selected = objects.find(o => o.id === selectedId) ?? objects[0];

  const handleCapability = (key: Capability) => {
    setCapability(key);
    setSelectedId(reviewObjects[key][0].id);
  };

  return (
    <div className="ai-review-page">
      <PageHeader
        title="AI审核"
        badge={{ text: 'Demo 预览', tone: 'warning' }}
        subtitle={`AI负责检查已经形成的内容是否正确、有效、合规。当前项目：${currentProject.name}`}
      />

      {/* 能力切换 */}
      <div className="review-capability-tabs">
        {capabilityTabs.map(tab => (
          <button
            key={tab.key}
            className={`review-capability-tab ${capability === tab.key ? 'active' : ''}`}
            onClick={() => handleCapability(tab.key)}
          >
            <span className="review-tab-name">{tab.label}</span>
            <span className="review-tab-desc">{tab.desc}</span>
          </button>
        ))}
      </div>

      {/* 工作台三栏：对象 | 正文 | 结果 */}
      <div className="review-workbench">
        <div className="review-objects">
          <h3 className="section-title">审核对象</h3>
          <div className="review-object-list">
            {objects.map(obj => (
              <button
                key={obj.id}
                className={`review-object-item ${selected?.id === obj.id ? 'active' : ''}`}
                onClick={() => setSelectedId(obj.id)}
              >
                <span className="review-object-name">{obj.name}</span>
                <span className="review-object-meta">{obj.meta}</span>
                <span className={`status-badge ${obj.status}`}>{obj.statusText}</span>
              </button>
            ))}
          </div>
          <p className="review-empty-hint">以上为示例审核对象，用于演示审核工作流。</p>
        </div>

        <div className="review-content surface-card">
          <h3 className="section-title">正文内容</h3>
          {selected && (
            <div className="review-content-body">
              {selected.content.split('\n').map((line, i) => (
                line.trim() ? <p key={i} className={line.startsWith('(') ? 'content-cue' : ''}>{line}</p> : <br key={i} />
              ))}
            </div>
          )}
        </div>

        <div className="review-result">
          {selected && (
            <FadeContent duration={0.25}>
              <div className="review-result-head">
                <h3>AI审核结果</h3>
                <span className={`status-badge ${selected.status}`}>{selected.statusText}</span>
              </div>

              {/* 综合评分条 */}
              <div className="score-block">
                <div className="score-row">
                  <span className="score-label">综合评分</span>
                  <span className="score-value">{selected.score} / 10</span>
                </div>
                <div className="score-bar">
                  <div className="score-bar-fill" style={{ width: `${selected.score * 10}%` }} />
                  <div className="score-bar-pass" style={{ width: '80%' }} title="上线建议线 8.0" />
                </div>
                <span className="score-hint">风险等级：{selected.riskLevel} · 竖线为 8.0 上线建议线</span>
              </div>

              {/* 检查项 */}
              <div className="review-checks">
                {selected.checks.map(c => (
                  <div className="review-check-row" key={c.label}>
                    <span className="review-check-label">{c.label}</span>
                    <span className={`review-check-value tone-${c.tone}`}>{c.value}</span>
                  </div>
                ))}
              </div>

              {/* 必须修改 / 优化建议 */}
              {selected.fixes.length > 0 && (
                <div className="review-fix-block warning-block">
                  <span className="review-field-label">必须修改</span>
                  <ul>{selected.fixes.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
              {selected.suggestions.length > 0 && (
                <div className="review-fix-block">
                  <span className="review-field-label">优化建议</span>
                  <ul>{selected.suggestions.map((sg, i) => <li key={i}>{sg}</li>)}</ul>
                </div>
              )}

              <p className="review-result-note">{selected.summary}</p>
              <p className="review-result-demo">以上为示例结果，展示未来审核输出的信息结构。</p>
            </FadeContent>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIReview;
